import axios, { AxiosInstance, AxiosStatic } from "axios";
import {
  getOriginalFileDimensions,
  getMaxDimensionsRetina,
  prepareFolderName,
  prepareFileName,
  getEmbedderHostname,
} from "@topol/utils";
import { IImage, ImagesSchema } from "../schemas/imageSchema";
import { getCurrentInstance, ref, Ref } from "vue";
import { createClient } from "../components/ImagePicker/StockImages/pexels-client/main";
import { readAndCompressImage } from "browser-image-resizer";
import type { Dimensions } from "@topol/utils";
import type { ImageUploadResponse } from "../../types/ApiResponses";
import type { FileOptions } from "../../types/FileOptions";
import emitter from "./../eventBus/eventBus";

type ApiParams = {
  hostname?: string;
  id?: string;
  uuid?: string;
  userId?: string;
  key?: string;
  path?: string;
};

export default (options: FileOptions, emit?: any) => {
  // Hosts may provide a pre-configured axios via globalProperties.$http
  // (email/landing-page editors register auth interceptors on the global
  // axios singleton); apps without that convention fall back to plain axios.
  const $http: AxiosStatic | AxiosInstance =
    getCurrentInstance()?.appContext.config.globalProperties.$http ?? axios;

  const supportedExtensions = "image/png,image/gif,image/jpeg,image/jpg";
  const supportedExtensionsArray = ["89504e47", "47494638"];
  const maxSize = options.imageMaxSize;

  const actualPath = ref("");
  const loading = ref(false);
  const httpClient: Ref<AxiosInstance> = ref($http);
  const items: Ref<IImage[]> = ref([]);
  const loadingNext = ref(false);
  const pexelsClient = createClient(options.pexelsApiKey || "");

  const getCommonParams = () => {
    const params: ApiParams = {};
    if (options.type == "plugin" || options.type === "ecomail") {
      params.hostname = getEmbedderHostname();
      params.id = options.id as string;
      params.uuid = options.userId;
      params.userId = options.userId;
    }

    return params;
  };

  const setHttpClient = () => {
    if (options.urls.lambda && options.urls.folders.startsWith(options.urls.lambda)) {
      const localAxiosInstance = axios.create();
      delete localAxiosInstance.defaults.headers["Authorization"];
      httpClient.value = localAxiosInstance;
    } else {
      httpClient.value = $http;
    }
  };

  async function filereaderLoad(file: File | Blob): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onload = (e: any) => resolve(new Uint8Array(e.target.result).subarray(0, 4));
      fileReader.onerror = (e) => {
        reject(e);
      };
      fileReader.readAsArrayBuffer(file);
    });
  }

  const isExtensionSupported = async (blob: File | Blob) => {
    try {
      const fileFromLoader = await filereaderLoad(blob);
      let header = "";
      for (let i = 0; i < fileFromLoader.length; i++) {
        header += fileFromLoader[i].toString(16);
      }
      if (supportedExtensionsArray.includes(header) || header.startsWith("ffd8ff")) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      console.error("Error reading file", e);
      return false;
    }
  };

  const exceedsSizeLimit = (file: File | Blob, limit: number) => {
    if (file.size > limit) {
      return true;
    }

    return false;
  };

  const fileAlreadyExists = async (fileName: string, path: string) => {
    const currentFiles = await fetchListing(path);

    if (currentFiles === null) {
      return true;
    }

    const strippedFileName = prepareFileName(fileName);

    const foundFile = currentFiles.find((f: any) => f.name === strippedFileName);

    if (foundFile !== undefined) {
      return true;
    }
    return false;
  };

  function getQualityCompression(mimeType: string): number {
    if (options.imageCompressionOptions) {
      if (mimeType === "image/jpeg") {
        return options.imageCompressionOptions.qualityJpeg;
      }

      if (mimeType === "image/png") {
        return options.imageCompressionOptions.qualityPng;
      }
    }

    return 1;
  }

  async function compressAndResizeImage(file: File) {
    let dimensions: Dimensions;

    try {
      dimensions = await getOriginalFileDimensions(file as File);
    } catch (err) {
      throw new Error(String(err));
    }

    let maxDimensions = dimensions;

    if (
      options.imageCompressionOptions &&
      options.imageCompressionOptions.enableAutoResize &&
      options.imageCompressionOptions.maxWidth
    ) {
      maxDimensions = getMaxDimensionsRetina(dimensions, options.imageCompressionOptions.maxWidth);
    }

    const quality = getQualityCompression(file.type);

    if (
      quality === 1 &&
      options.imageCompressionOptions &&
      options.imageCompressionOptions.enableAutoResize === false
    ) {
      return file;
    }

    const compressedImage = await readAndCompressImage(file, {
      quality: quality,
      maxWidth: maxDimensions.width,
      maxHeight: maxDimensions.heigth,
      mimeType: file.type,
    });

    // Sometimes the compressed image is bigger than the original
    // because the image is compressed before hand and then compressed again
    // with higher quality rate than the  original
    return compressedImage.size < file.size ? compressedImage : file;
  }

  const _uploadFile = async (
    f: File | Blob,
    path = "/",
    fileName = f instanceof File ? f.name : "",
    prefetchedListing?: IImage[],
    uploadedNames?: Set<string>,
  ) => {
    if (f == null) {
      emit("notify", "no_file_picked");
      throw new Error("No file has been picked.");
    }

    let file: File | Blob = f;

    if (!(await isExtensionSupported(file))) {
      emit("notify", "extension_error");
      throw new Error("extension_error");
    }

    if (f.type !== "image/gif") {
      file = await compressAndResizeImage(f as File);
    }

    if (exceedsSizeLimit(file, maxSize)) {
      emit("notify", "max_size_exceeded");
      throw new Error("max_size_exceeded");
    }

    if (path === "") {
      path = "/";
    }

    const normalizedFileName = prepareFileName(fileName);

    const existsOnServer = prefetchedListing
      ? prefetchedListing.some((item: IImage) => item.name === normalizedFileName)
      : await fileAlreadyExists(normalizedFileName, path);

    const existsInBatch = uploadedNames?.has(normalizedFileName) ?? false;

    if (existsOnServer || existsInBatch) {
      emit("notify", "file_already_exists:" + normalizedFileName);
      throw new Error("file_already_exists");
    }

    // Reserve the name immediately to prevent parallel uploads of the same file
    uploadedNames?.add(normalizedFileName);

    if (
      (options.type == "plugin" || options.type === "ecomail") &&
      options.urls.imageUpload == "upload" &&
      options.urls.GCS_SIGNED_URL
    ) {
      const localAxiosInstance = axios.create();
      delete localAxiosInstance.defaults.headers["Authorization"];

      try {
        const params = {
          name: options.userId + path + normalizedFileName,
          type: file.type,
          path: path,
          uuid: options.userId,
        };

        const data = await localAxiosInstance.get<ImageUploadResponse>(
          options.urls.GCS_SIGNED_URL + options.apiKey,
          {
            params: params,
          },
        );

        await localAxiosInstance.put(data.data.uploadUrl, file, {
          headers: {
            Accept: "*/*",
            "Content-Type": file.type,
            "x-goog-acl": "public-read",
            ...(data.data.uploadHeaders ?? {}),
          },
        });

        const response = {
          xhr: {
            response: JSON.stringify({
              name: normalizedFileName,
              url: data.data.url,
            }),
          },
          size: file.size,
          name: normalizedFileName,
          rootPath: path,
          file: {
            extension: file.type,
          },
        };

        return response;
      } catch (e: any) {
        uploadedNames?.delete(normalizedFileName);
        emit("notify", "upload_error:" + normalizedFileName);
        console.error(e);
        throw new Error(e.message);
      }
    } else {
      try {
        const formData = new FormData();

        formData.append("image", file, normalizedFileName);
        formData.append("path", path);
        formData.append("uuid", options.userId);

        const res = await $http.post(options.urls.imageUpload || "/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        const resolvedName = res.data.name;
        const resolvedUrl = res.data.url;

        if (res.data.success === true) {
          const response = {
            xhr: {
              response: JSON.stringify({
                name: resolvedName,
                url: resolvedUrl,
              }),
            },
            size: file.size,
            name: resolvedName,
            rootPath: path,
            file: {
              extension: file.type,
            },
          };
          return response;
        }

        // 2xx response without success: true (e.g. server-side size limit)
        // is still a failed upload. Message is for the console only,
        // the user gets the translated upload_error toast from catch.
        throw new Error(
          typeof res.data?.message === "string"
            ? res.data.message
            : "Upload endpoint responded 2xx without success: true",
        );
      } catch (e: any) {
        uploadedNames?.delete(normalizedFileName);
        emit("notify", "upload_error:" + normalizedFileName);
        console.error(e);
        throw new Error(e.message);
      }
    }
  };

  const loadFoldersFromApi = async (path: string, force = false) => {
    if (path != actualPath.value || force) {
      loading.value = true;

      const params: ApiParams = { path: path };
      if (options.type == "plugin" || options.type == "ecomail") {
        params.path = path;
        params.hostname = getEmbedderHostname();
        params.userId = options.userId;
        params.uuid = options.userId;
      }

      //Change URL for backwards compat
      //Vue resource was able to use wildcard in url like foldercontents/{key}
      //axios can't do that, so fix is needed to here for the api to work like it used to
      let apiFolderURL = "";
      if (options.urls.folders.includes("{key}")) {
        apiFolderURL = options.urls.folders.replace("{key}", options.apiKey);
      } else {
        apiFolderURL = options.urls.folders;
        params.key = options.apiKey;
      }

      try {
        const res = await httpClient.value.get(apiFolderURL, {
          params: params,
        });
        actualPath.value = path;

        const fetchedItems = res.data.items || res.data;

        const { error, data, success } = ImagesSchema.safeParse(fetchedItems);

        if (!success) {
          console.error("Error loading images", error.issues);
          items.value = [];
          return false;
        } else {
          items.value = data;
        }

        loading.value = false;
      } catch (error) {
        console.error("Error loading images", error);
        loading.value = false;
      }

      return items.value;
    }
  };

  const removeSelectionApi = async (folders: any) => {
    const params: ApiParams = getCommonParams();

    //Change URL for backwards compat
    //Vue resource was able to use wildcard in url like foldercontents/{key}
    //axios can't do that, so fix is needed to here for the api to work like it used to
    let apiFolderURL = "";
    if (options.urls.folders.includes("{key}")) {
      apiFolderURL = options.urls.folders.replace("{key}", options.apiKey);
    } else {
      apiFolderURL = options.urls.folders;
      params.key = options.apiKey;
    }

    folders = folders.filter((f: any) => f.name !== "");

    try {
      await httpClient.value.post(apiFolderURL + "/delete", folders, {
        params: params,
      });
    } catch (error) {
      console.error("Error removing items", error);
    } finally {
      // removingloading.value = false;
    }
  };

  const createFolderRemoteApi = async (folder: IImage) => {
    const params: ApiParams = getCommonParams();

    //remove malicious chars
    folder.name = prepareFolderName(folder.name);

    if (folder.name === "") {
      throw new Error("folder_name_empty");
    }

    //Change URL for backwards compat
    //Vue resource was able to use wildcard in url like foldercontents/{key}
    //axios can't do that, so fix is needed to here for the api to work like it used to
    let apiFolderURL = "";

    if (options.urls.folders.includes("{key}")) {
      apiFolderURL = options.urls.folders.replace("{key}", options.apiKey);
    } else {
      apiFolderURL = options.urls.folders;

      params.key = options.apiKey;
    }

    try {
      await httpClient.value.post(
        apiFolderURL,
        {
          name: folder.name,
          path: options.type !== "plugin" ? folder.path : "/" + options.userId + folder.path,
        },
        { params: params },
      );
      emitter.emit("folder-created");
    } catch (error) {
      console.error("Error creating folder", error);
    }
  };

  const getPixelsPhotos = async (
    query: string,
    perPage: number,
    currentPage: number,
    locale: string,
    dimension: string,
  ) => {
    loadingNext.value = true;

    try {
      //@ts-expect-error
      const response: PhotosWithTotalResults = await pexelsClient.photos.search({
        query: query,
        per_page: perPage,
        page: currentPage,
        locale: locale,
        orientation: dimension,
      });
      //   totalItemsForCurrentQuery.value = response.total_results;
      return response;
    } catch (err) {
      console.error("Getting images failed");
    } finally {
      loadingNext.value = false;
    }
  };

  const fetchListing = async (path: string): Promise<IImage[] | null> => {
    const params: any = { path };
    if (options.type == "plugin" || options.type == "ecomail") {
      params.hostname = getEmbedderHostname();
      params.userId = options.userId;
      params.uuid = options.userId;
    }

    //Change URL for backwards compat
    //Vue resource was able to use wildcard in url like foldercontents/{key}
    //axios can't do that, so fix is needed to here for the api to work like it used to
    let apiFolderURL = "";
    if (options.urls.folders.includes("{key}")) {
      apiFolderURL = options.urls.folders.replace("{key}", options.apiKey);
    } else {
      apiFolderURL = options.urls.folders;
      params.key = options.apiKey;
    }

    let http = $http;
    if (options.urls.lambda && options.urls.folders.startsWith(options.urls.lambda)) {
      const localAxiosInstance = axios.create();
      delete localAxiosInstance.defaults.headers["Authorization"];
      http = localAxiosInstance;
    }

    let res: any;

    try {
      res = await http.get(apiFolderURL, {
        params: params,
      });
    } catch (e: any) {
      console.error("Error loading images", e);
      return null;
    }
    const fetchedItems = res.data.items || res.data;
    const { data, success, error } = ImagesSchema.safeParse(fetchedItems);

    if (!success) {
      console.error("Error loading images", error.issues);
      return null;
    }
    return data;
  };

  return {
    supportedExtensions,
    actualPath,
    loading,
    items,
    loadingNext,
    _uploadFile,
    fetchListing,
    setHttpClient,
    loadFoldersFromApi,
    removeSelectionApi,
    createFolderRemoteApi,
    getPixelsPhotos,
  };
};
